import React, { useState, useEffect } from 'react';
import { positionApi } from '../../api/positionApi';
import { Position, CreatePositionRequest } from '../../types/position.type';
import { Worker } from '../../types/worker.type';
import { Button, Card, Form, Input, Modal, Select, Spin, Tag, Typography, Space, message, InputNumber, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DragOutlined, PrinterOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/auth.context';
import useGetRequest from '../../hooks/useGetRequest';
import { canEditPositions } from '../../utils/roleUtils';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title, Text } = Typography;

interface WorkerOption {
    value: string;
    label: string;
}

interface FormShift {
    shiftName: string;
    workerIds: WorkerOption[];
}

interface PositionFormData {
    name: string;
    description?: string;
    shifts: FormShift[];
    additionalWorkers: WorkerOption[];
    requiredWorkersCount: number;
}

interface SortablePositionCardProps {
    position: Position;
    onEdit: (position: Position) => void;
    onDelete: (id: string) => void;
    onRemoveWorker: (positionId: string, workerId: string) => void;
    calculateMissingWorkers: (position: Position) => number;
}

const SortablePositionCard: React.FC<SortablePositionCardProps> = ({
    position,
    onEdit,
    onDelete,
    onRemoveWorker,
    calculateMissingWorkers,
    }) => {
    const { authState } = useAuth();
    const isAdmin = canEditPositions(authState.user?.role);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: position._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // --- Danger-теги для смен ---
    const totalAssigned = position.shifts.reduce((acc, shift) => acc + shift.workerIds.length, 0);
    const totalMissing = Math.max(0, (position.requiredWorkersCount || 0) - totalAssigned);
    let remainingMissing = totalMissing;
    const shiftMissingArr = position.shifts.map(() => 0);
    let i = 0;
    while (remainingMissing > 0 && position.shifts.length > 0) {
        shiftMissingArr[i % position.shifts.length]++;
        remainingMissing--;
        i++;
    }

    // --- Danger-теги для дополнительных работников ---
    const additionalAssigned = position.additionalWorkers.length;
    const additionalRequired = position.requiredAdditionalWorkersCount || 0;
    const additionalMissing = Math.max(0, additionalRequired - additionalAssigned);

    return (
        <div ref={setNodeRef} style={style}>
            <Card
                style={{
                    marginBottom: 12,
                    border: isDragging ? '2px dashed #1890ff' : undefined,
                }}
                title={
                    <Space>
                        {isAdmin && (
                            <div {...attributes} {...listeners} style={{ cursor: 'grab' }}>
                                <DragOutlined style={{ color: '#999' }} />
                            </div>
                        )}
                        <Text strong>{position.name}</Text>
                        {(totalMissing > 0 || additionalMissing > 0) && (
                            <Tag style={{ color: 'red', fontWeight: 'bold', background: '#fff1f0', border: '1px solid #ff4d4f' }}>
                                {totalMissing > 0 && `Не хватает ${totalMissing} сотрудник${totalMissing > 1 ? 'ов' : 'а'} в сменах`}
                                {totalMissing > 0 && additionalMissing > 0 && ', '}
                                {additionalMissing > 0 && `Не хватает ${additionalMissing} доп. сотрудник${additionalMissing > 1 ? 'ов' : 'а'}`}
                            </Tag>
                        )}
                    </Space>
                }
                extra={
                    isAdmin && (
                        <Space>
                            <Button icon={<EditOutlined />} onClick={() => onEdit(position)}>
                                Редактировать
                            </Button>
                            <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(position._id)}>
                                Удалить
                            </Button>
                        </Space>
                    )
                }
            >
                {position.description && <Text type="secondary">{position.description}</Text>}
                <div style={{ marginTop: 12 }}>
                    <Text strong>Требуемое количество сотрудников (по сменам): {position.requiredWorkersCount}</Text>
                </div>
                <div style={{ marginTop: 4 }}>
                    <Text strong>Требуемое количество дополнительных сотрудников: {position.requiredAdditionalWorkersCount}</Text>
                </div>
                <div style={{ marginTop: 12 }}>
                    <Text strong>Смены:</Text>
                    {position.shifts.map((shift, idx) => (
                        <div key={idx} style={{ margin: '8px 0' }}>
                            <Text>{shift.shiftName}:</Text>
                            <Space wrap>
                                {shift.workerIds.map(workerId => (
                                    isAdmin ? (
                                        <Popconfirm
                                            key={workerId._id}
                                            title="Открепить сотрудника?"
                                            okText="Да"
                                            cancelText="Нет"
                                            onConfirm={() => onRemoveWorker(position._id, workerId._id)}
                                        >
                                            <Tag
                                                color="blue"
                                                closable
                                                onClose={() => onRemoveWorker(position._id, workerId._id)}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    border: '2px solid #0050b3',
                                                    margin: '2px 4px',
                                                    padding: '4px 8px'
                                                }}
                                            >
                                                {workerId.name} {workerId.surname}{workerId.nickname ? ` (${workerId.nickname})` : ''}
                                            </Tag>
                                        </Popconfirm>
                                    ) : (
                                        <Tag
                                            key={workerId._id}
                                            color="blue"
                                            style={{ 
                                                border: '2px solid #0050b3',
                                                margin: '2px 4px',
                                                padding: '4px 8px'
                                            }}
                                        >
                                            {workerId.name} {workerId.surname}{workerId.nickname ? ` (${workerId.nickname})` : ''}
                                        </Tag>
                                    )
                                ))}
                                {/* danger-теги после сотрудников */}
                                {Array.from({ length: shiftMissingArr[idx] }).map((_, i) => (
                                    <Tag key={`missing-${i}`} color="red" style={{ 
                                        background: '#fff1f0', 
                                        border: '2px solid #cf1322', 
                                        color: '#cf1322', 
                                        fontWeight: 600,
                                        margin: '2px 4px',
                                        padding: '4px 8px'
                                    }}>
                                        Нет сотрудника
                                    </Tag>
                                ))}
                            </Space>
                        </div>
                    ))}
                </div>
                <div style={{ marginTop: 12 }}>
                    <Text strong>Дополнительные работники:</Text>
                    <Space wrap>
                        {position.additionalWorkers.map(workerId => (
                            isAdmin ? (
                                <Popconfirm
                                    key={workerId._id}
                                    title="Открепить сотрудника?"
                                    okText="Да"
                                    cancelText="Нет"
                                    onConfirm={() => onRemoveWorker(position._id, workerId._id)}
                                >
                                    <Tag
                                        color="purple"
                                        closable
                                        onClose={() => onRemoveWorker(position._id, workerId._id)}
                                        style={{ 
                                            cursor: 'pointer',
                                            border: '2px solid #531dab',
                                            margin: '2px 4px',
                                            padding: '4px 8px'
                                        }}
                                    >
                                        {workerId.name} {workerId.surname}{workerId.nickname ? ` (${workerId.nickname})` : ''}
                                    </Tag>
                                </Popconfirm>
                            ) : (
                                <Tag
                                    key={workerId._id}
                                    color="purple"
                                    style={{ 
                                        border: '2px solid #531dab',
                                        margin: '2px 4px',
                                        padding: '4px 8px'
                                    }}
                                >
                                    {workerId.name} {workerId.surname}{workerId.nickname ? ` (${workerId.nickname})` : ''}
                                </Tag>
                            )
                        ))}
                        {additionalMissing > 0 && Array.from({ length: additionalMissing }).map((_, i) => (
                            <Tag key={`missing-additional-${i}`} color="red" style={{ 
                                background: '#fff1f0', 
                                border: '2px solid #cf1322', 
                                color: '#cf1322', 
                                fontWeight: 600,
                                margin: '2px 4px',
                                padding: '4px 8px'
                            }}>
                                Нет сотрудника
                            </Tag>
                        ))}
                    </Space>
                </div>
            </Card>
        </div>
    );
};

const PositionsPage: React.FC = () => {
    const [positions, setPositions] = useState<Position[]>([]);
    const [workers, setWorkers] = useState<Worker[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingPosition, setEditingPosition] = useState<Position | null>(null);
    const [form] = Form.useForm<any>();
    const { authState } = useAuth();
    const { data: workerList } = useGetRequest("/worker/list")
    const isAdmin = canEditPositions(authState.user?.role);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [positionsData, workersData] = await Promise.all([
                positionApi.getPositions(),
                positionApi.getWorkersForPosition(),
            ]);
            setPositions(positionsData);
            setWorkers(workersData);
        } catch (error) {
            message.error('Ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (position?: Position) => {
        setEditingPosition(position || null);
        if (position) {
            form.setFieldsValue({
                name: position.name,
                description: position.description || '',
                shifts: position.shifts.map(shift => ({
                    shiftName: shift.shiftName,
                    workerIds: shift.workerIds.map((worker: any) => ({
                        value: worker._id,
                        label: `${worker.name} ${worker.surname}${worker.nickname ? ` (${worker.nickname})` : ''}`
                    }))
                })),
                additionalWorkers: position.additionalWorkers.map((worker: any) => ({
                    value: worker._id,
                    label: `${worker.name} ${worker.surname}${worker.nickname ? ` (${worker.nickname})` : ''}`
                })),
                requiredWorkersCount: position.requiredWorkersCount,
                requiredAdditionalWorkersCount: position.requiredAdditionalWorkersCount,
            });
        } else {
            form.resetFields();
        }
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingPosition(null);
        form.resetFields();
    };

    const handleSubmit = async (values: CreatePositionRequest) => {
        setSubmitting(true)
        try {
            // Преобразуем данные из labelInValue формата в обычный формат для отправки
            const processedValues = {
                ...values,
                shifts: values.shifts?.map(shift => ({
                    shiftName: shift.shiftName,
                    workerIds: shift.workerIds?.map((worker: any) =>
                        typeof worker === 'string' ? worker : worker.value
                    ) || []
                })) || [],
                additionalWorkers: values.additionalWorkers?.map((worker: any) =>
                    typeof worker === 'string' ? worker : worker.value
                ) || [],
                requiredWorkersCount: values.requiredWorkersCount,
                requiredAdditionalWorkersCount: values.requiredAdditionalWorkersCount,
            };

            if (editingPosition) {
                await positionApi.updatePosition(editingPosition._id, processedValues);
                message.success('Должность обновлена');
            } else {
                await positionApi.createPosition(processedValues);
                message.success('Должность создана');
            }
            closeModal();
            loadData();
        } catch (error: any) {
            message.error(error.response?.data?.error || 'Произошла ошибка');
        } finally {
            setSubmitting(false)
        }
    };

    const handleDelete = async (id: string) => {
        Modal.confirm({
            title: 'Удалить должность?',
            content: 'Вы уверены, что хотите удалить эту должность?',
            okText: 'Удалить',
            cancelText: 'Отмена',
            okType: 'danger',
            onOk: async () => {
                try {
                    await positionApi.deletePosition(id);
                    message.success('Должность удалена');
                    loadData();
                } catch (error: any) {
                    message.error(error.response?.data?.error || 'Ошибка при удалении');
                }
            },
        });
    };

    const handleRemoveWorker = async (positionId: string, workerId: string) => {
        Modal.confirm({
            title: 'Открепить сотрудника?',
            content: 'Вы уверены, что хотите открепить этого сотрудника от должности?',
            okText: 'Открепить',
            cancelText: 'Отмена',
            okType: 'danger',
            onOk: async () => {
                try {
                    await positionApi.removeWorkerFromPosition(positionId, workerId);
                    message.success('Сотрудник откреплен от должности');
                    loadData();
                } catch (error: any) {
                    message.error(error.response?.data?.error || 'Ошибка при откреплении');
                }
            },
        });
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setPositions((items) => {
                const oldIndex = items.findIndex((item) => item._id === active.id);
                const newIndex = items.findIndex((item) => item._id === over?.id);

                const newPositions = arrayMove(items, oldIndex, newIndex);

                // Update sort order in backend
                const updatedPositions = newPositions.map((position, index) => ({
                    id: position._id,
                    sortOrder: index,
                }));

                // Send update to backend
                positionApi.updateSortOrder(updatedPositions).catch((error) => {
                    message.error('Ошибка при обновлении порядка сортировки');
                    console.error('Error updating sort order:', error);
                });

                return newPositions;
            });
        }
    };



    const calculateMissingWorkers = (position: Position) => {
        const totalAssignedWorkers = position.shifts.reduce((total, shift) => {
            return total + shift.workerIds.length;
        }, 0) + position.additionalWorkers.length;

        const missingCount = position.requiredWorkersCount - totalAssignedWorkers;
        return Math.max(0, missingCount);
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        // Подсчет статистики
        const totalPositions = positions.length;
        const totalWorkers = workers.length;
        const totalAssignedWorkers = positions.reduce((total, position) => {
            return total + position.shifts.reduce((shiftTotal, shift) => {
                return shiftTotal + shift.workerIds.length;
            }, 0) + position.additionalWorkers.length;
        }, 0);
        const totalMissingWorkers = positions.reduce((total, position) => {
            return total + Math.max(0, (position.requiredWorkersCount || 0) - position.shifts.reduce((acc, shift) => acc + shift.workerIds.length, 0));
        }, 0);
        const totalMissingAdditionalWorkers = positions.reduce((total, position) => {
            return total + Math.max(0, (position.requiredAdditionalWorkersCount || 0) - position.additionalWorkers.length);
        }, 0);

        // Создание HTML для печати (схема)
        const printHtml = `
            <html>
                <head>
                    <title>Схема должностей</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            font-size: 12px;
                            color: #222;
                            margin: 20px;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 30px;
                            border-bottom: 2px solid #333;
                            padding-bottom: 10px;
                        }
                        .header h1 {
                            margin: 0;
                            font-size: 18px;
                            font-weight: bold;
                        }
                        .header .date {
                            margin-top: 5px;
                            font-size: 11px;
                            color: #666;
                        }
                        .summary {
                            margin-bottom: 30px;
                            padding: 10px;
                            background: #f9f9f9;
                            border-radius: 5px;
                            border: 1px solid #bbb;
                            display: flex;
                            gap: 30px;
                            justify-content: center;
                        }
                        .summary-item {
                            font-size: 13px;
                            font-weight: bold;
                            text-align: center;
                        }
                        .positions-scheme {
                            display: flex;
                            flex-direction: column;
                            gap: 32px;
                        }
                        .position-block {
                            border: 2px solid #222;
                            border-radius: 8px;
                            margin-bottom: 0;
                            padding: 0;
                            page-break-inside: avoid;
                        }
                        .position-header {
                            background: #f5f5f5;
                            border-bottom: 1px solid #bbb;
                            padding: 10px 16px 6px 16px;
                            display: flex;
                            flex-wrap: wrap;
                            align-items: baseline;
                            gap: 18px;
                        }
                        .position-title {
                            font-size: 15px;
                            font-weight: bold;
                            margin-right: 18px;
                        }
                        .position-desc {
                            font-size: 11px;
                            color: #666;
                            margin-left: 10px;
                        }
                        .position-req {
                            font-size: 11px;
                            color: #333;
                            margin-left: 10px;
                        }
                        .table-scheme {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 0;
                            margin-bottom: 10px;
                        }
                        .table-scheme th, .table-scheme td {
                            border: 1px solid #222;
                            padding: 4px 8px;
                            text-align: left;
                            font-size: 12px;
                        }
                        .table-scheme th {
                            background: #f0f0f0;
                            font-weight: bold;
                            text-align: center;
                        }
                        .danger-cell {
                            background: #fff1f0 !important;
                            color: #ff4d4f !important;
                            font-weight: bold;
                            text-align: center;
                        }
                        .nowrap {
                            white-space: nowrap;
                        }
                        .worker-name {
                            display: inline-block;
                            padding: 2px 6px;
                            margin: 1px 2px;
                            background: #f0f8ff;
                            border: 1px solid #d6e4ff;
                            border-radius: 3px;
                            font-size: 11px;
                        }
                        .worker-name-danger {
                            background: #fff1f0 !important;
                            border: 1px solid #ff4d4f !important;
                            color: #ff4d4f !important;
                            font-weight: bold;
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>СХЕМА ДОЛЖНОСТЕЙ</h1>
                        <div class="date">Дата: ${new Date().toLocaleDateString('ru-RU')}</div>
                    </div>
                    <div class="summary">
                        <div class="summary-item">Должностей: ${totalPositions}</div>
                        <div class="summary-item">Всего работников: ${totalWorkers}</div>
                        <div class="summary-item">Назначено: ${totalAssignedWorkers}</div>
                        <div class="summary-item">Не хватает в сменах: ${totalMissingWorkers}</div>
                        <div class="summary-item">Не хватает доп.: ${totalMissingAdditionalWorkers}</div>
                    </div>
                    <div class="positions-scheme">
                        ${positions.map((position, idx) => {
                            // danger-теги для смен
                            const totalAssigned = position.shifts.reduce((acc, shift) => acc + shift.workerIds.length, 0);
                            const totalMissing = Math.max(0, (position.requiredWorkersCount || 0) - totalAssigned);
                            let remainingMissing = totalMissing;
                            const shiftMissingArr = position.shifts.map(() => 0);
                            let i = 0;
                            while (remainingMissing > 0 && position.shifts.length > 0) {
                                shiftMissingArr[i % position.shifts.length]++;
                                remainingMissing--;
                                i++;
                            }
                            // danger-теги для дополнительных
                            const additionalAssigned = position.additionalWorkers.length;
                            const additionalRequired = position.requiredAdditionalWorkersCount || 0;
                            const additionalMissing = Math.max(0, additionalRequired - additionalAssigned);
                            return `
                            <div class="position-block">
                                <div class="position-header">
                                    <span class="position-title">${position.name}</span>
                                    <span class="position-req">Смен: <b>${position.shifts.length}</b></span>
                                    <span class="position-req">Треб. по сменам: <b>${position.requiredWorkersCount}</b></span>
                                    <span class="position-req">Треб. доп.: <b>${position.requiredAdditionalWorkersCount}</b></span>
                                    ${position.description ? `<span class="position-desc">${position.description}</span>` : ''}
                                </div>
                                <table class="table-scheme">
                                    <thead>
                                        <tr>
                                            <th class="nowrap">Смена</th>
                                            <th>Сотрудники</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${position.shifts.map((shift, sidx) => {
                                            const allWorkers = [
                                                ...shift.workerIds.map(worker => `<span class="worker-name">${worker.name} ${worker.surname}${worker.nickname ? ` (${worker.nickname})` : ''}</span>`),
                                                ...Array.from({ length: shiftMissingArr[sidx] }).map(() => `<span class="worker-name worker-name-danger">Нет сотрудника</span>`)
                                            ];
                                            return `
                                                <tr>
                                                    <td class="nowrap">${shift.shiftName}</td>
                                                    <td>${allWorkers.length > 0 ? allWorkers.join('') : '<span class="worker-name worker-name-danger">Нет сотрудника</span>'}</td>
                                                </tr>
                                            `;
                                        }).join('')}
                                    </tbody>
                                </table>
                                <table class="table-scheme">
                                    <thead>
                                        <tr>
                                            <th style="width:40px">№</th>
                                            <th>Дополнительный работник</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${[...position.additionalWorkers.map((worker, i) => `
                                            <tr><td>${i + 1}</td><td><span class="worker-name">${worker.name} ${worker.surname}${worker.nickname ? ` (${worker.nickname})` : ''}</span></td></tr>
                                        `),
                                        ...Array.from({ length: additionalMissing }).map((_, i) => `
                                            <tr><td>${position.additionalWorkers.length + i + 1}</td><td><span class="worker-name worker-name-danger">Нет сотрудника</span></td></tr>
                                        `)
                                        ].join('')}
                                    </tbody>
                                </table>
                            </div>
                            `;
                        }).join('')}
                    </div>
                </body>
            </html>
        `;

        printWindow.document.write(printHtml);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    const renderPositions = () => (
        <Space direction="vertical" style={{ width: '100%' }} size={24}>
            {positions.map(position => (
                <SortablePositionCard
                    key={position._id}
                    position={position}
                    onEdit={openModal}
                    onDelete={handleDelete}
                    onRemoveWorker={handleRemoveWorker}
                    calculateMissingWorkers={calculateMissingWorkers}
                />
            ))}
        </Space>
    );

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 24 }}>
                <Title level={2}>Управление должностями</Title>
                <Space>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrint}
                        style={{ marginRight: 8 }}
                    >
                        Печать отчета
                    </Button>
                    {isAdmin && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
                            Создать должность
                        </Button>
                    )}
                </Space>
            </Space>

            <Modal
                open={modalOpen}
                title={editingPosition ? 'Редактировать должность' : 'Создать должность'}
                onCancel={closeModal}
                footer={null}
                destroyOnClose
                width={700}
                confirmLoading={submitting}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ shifts: [], additionalWorkers: [], requiredWorkersCount: 1, requiredAdditionalWorkersCount: 0 }}
                >
                    <Form.Item
                        name="name"
                        label="Название должности"
                        rules={[{ required: true, message: 'Введите название' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Описание"
                    >
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item
                        name="requiredWorkersCount"
                        label="Требуемое количество сотрудников (по сменам)"
                        rules={[{ required: true, message: 'Введите количество' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="requiredAdditionalWorkersCount"
                        label="Требуемое количество дополнительных сотрудников"
                        rules={[{ required: true, message: 'Введите количество' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.List name="shifts">
                        {(fields, { add, remove }) => (
                            <div>
                                <Text strong>Смены:</Text>
                                {fields.map((field, idx) => (
                                    <Card
                                        key={field.name}
                                        size="small"
                                        style={{ marginBottom: 12 }}
                                        title={
                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'shiftName']}
                                                rules={[{ required: true, message: 'Название смены' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input placeholder="Название смены" />
                                            </Form.Item>
                                        }
                                        extra={
                                            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                        }
                                    >
                                        <Form.Item
                                            name={[field.name, 'workerIds']}
                                            label="Работники в смене"
                                        // rules={[{ required: true, message: 'Выберите работников' }]}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Выберите работников"
                                                optionFilterProp="children"
                                                showSearch
                                                maxTagCount={4}
                                                labelInValue
                                            >
                                                {workers.map(worker => (
                                                    <Select.Option key={worker._id} value={worker._id}>
                                                        {worker.name} {worker.surname} {worker.nickname ? ` (${worker.nickname})` : ''}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Добавить смену</Button>
                            </div>
                        )}
                    </Form.List>
                    <Form.Item name="additionalWorkers" label="Дополнительные работники">
                        <Select
                            mode="multiple"
                            placeholder="Выберите дополнительных работников"
                            optionFilterProp="children"
                            showSearch
                            maxTagCount={4}
                            labelInValue
                        >
                            {workers.map(worker => (
                                <Select.Option key={worker._id} value={worker._id}>
                                    {worker.name} {worker.surname} {worker.nickname ? ` (${worker.nickname})` : ''}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingPosition ? 'Обновить' : 'Создать'}
                            </Button>
                            <Button onClick={closeModal}>Отмена</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {isAdmin ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={positions.map(pos => pos._id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {renderPositions()}
                    </SortableContext>
                </DndContext>
            ) : (
                renderPositions()
            )}
        </div>
    );
};

export default PositionsPage; 